import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  Button,
  Col,
  Divider,
  Form,
  Input,
  message,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Typography,
} from 'antd';
import {DeleteOutlined} from '@ant-design/icons';
import { toSnakeCase } from './csvUtils';
import CSVImportButton from './widgets/CSVImportButton';
import {useApiDelete, useApiGet, useApiPost} from "../../hooks/useApi";

const { Title } = Typography;

const PARAMETER_LABELS = {
  startAt: 'Date de d\u00e9but',
  endAt: 'Date de fin',
  tenant: 'Client',
  amount: 'Montant',
  lot_id: 'Lot',
  format_date: 'Format de date',
};

const DATE_FORMAT_OPTIONS = [
  { label: 'Français : JJ/MM/AAAA (31/01/2026)', value: 'd/m/Y' },
  { label: 'Français avec heure : JJ/MM/AAAA HH:mm (31/01/2026 14:30)', value: 'd/m/Y H:i' },
  { label: 'Français : JJ-MM-AAAA (31-01-2026)', value: 'd-m-Y' },
  { label: 'International (ISO) : AAAA-MM-JJ (2026-01-31)', value: 'Y-m-d' },
  { label: 'International (ISO) avec heure : AAAA-MM-JJ HH:mm:ss (2026-01-31 14:30:00)', value: 'Y-m-d H:i:s' },
  { label: 'Anglais : MM/JJ/AAAA (01/31/2026)', value: 'm/d/Y' },
];

// Libellé humain affiché dans le tableau des imports pour la valeur brute
// du paramètre format_date (pattern Carbon/PHP, cf. api)
const DATE_FORMAT_DISPLAY_LABELS = DATE_FORMAT_OPTIONS.reduce(
  (acc, option) => ({ ...acc, [option.value]: option.label }),
  {}
);

const parameterColumns = [
  {
    title: 'Param\u00e8tre',
    dataIndex: 'label',
    key: 'label',
  },
  {
    title: 'Colonne CSV',
    dataIndex: 'value',
    key: 'value',
  },
];

const DeleteImportButton = ({id, title, onDeleted}) => {
  const {execute: apiDelete, loading} = useApiDelete(`imports/${id}`);

  const handleDelete = () => apiDelete().then((response) => {
    if (response?.status) {
      message.success("Import supprimé!");
      onDeleted(id);
    }

    return response;
  });

  return (
    <Popconfirm
      title={`Supprimer l'import \u00ab ${title} \u00bb ?`}
      okText="Supprimer"
      cancelText="Annuler"
      okButtonProps={{danger: true}}
      onConfirm={handleDelete}
    >
      <Button
        danger
        size="small"
        loading={loading}
        icon={<DeleteOutlined />}
        aria-label={`Supprimer l'import ${title}`}
      />
    </Popconfirm>
  );
};

const getParameterRows = (parameters = {}) => Object.entries(parameters).map(
  ([key, value]) => ({
    key,
    label: PARAMETER_LABELS[key] ?? key,
    value: key === 'format_date' ? (DATE_FORMAT_DISPLAY_LABELS[value] ?? value) : value,
  })
);

const ParamsComponent = () => {
  const [form] = Form.useForm();
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [imports, setImports] = useState([]);

  const {execute: apiPost, loading: loadingPost} = useApiPost('imports');
  const {execute: apiGet, data, loading: loadingGet} = useApiGet('imports');

  useEffect(() => {
    apiGet();
  }, [apiGet]);

  useEffect(() => {
    if (Array.isArray(data?.imports)) {
      setImports(data.imports);
    }
  }, [data]);

  const headerOptions = useMemo(
    () => csvHeaders.map((header) => ({ label: header, value: header })),
    [csvHeaders]
  );

  const handleImportDeleted = useCallback((deletedImportId) => {
    setImports((currentImports) => currentImports.filter(
      (importItem) => importItem.id !== deletedImportId
    ));
  }, []);

  const importColumns = useMemo(() => [
    {
      title: 'Titre',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: '',
      key: 'actions',
      align: 'right',
      width: 100,
      render: (_, record) => (
        <DeleteImportButton
          id={record.id}
          title={record.title}
          onDeleted={handleImportDeleted}
        />
      ),
    },
  ], [handleImportDeleted]);

  const handleImportConfirm = ({ uniqueHeaders }) => {
    setCsvHeaders(uniqueHeaders);
    form.resetFields(['startAt', 'endAt', 'tenant', 'amount', 'lot_id', 'format_date']);
  };

  const onFinish = (values) => {
    const jsonData = {
      title: values.title,
      format_date: values.format_date,
      parameters: {
        startAt: toSnakeCase(values.startAt),
        endAt: toSnakeCase(values.endAt),
        tenant: toSnakeCase(values.tenant),
        amount: toSnakeCase(values.amount),
        lot_id: toSnakeCase(values.lot_id),
      },
    };

    apiPost(jsonData).then((response) => {
      if (response?.status) {
        message.success("Paramètres d'import sauvegardés!");
        if (response.import) {
          setImports((currentImports) => [
            ...currentImports,
            response.import,
          ]);
        }

        form.resetFields();
      }
    });
  };

  return (
    <div style={{ padding: 10 }}>
      <Row>
        <Space direction="vertical" size={0}>
          <Title
            level={2}
            style={{
              margin: 0,
              color: '#1E2A4A',
            }}
          >
            Gestion des opérateurs numériques
          </Title>
        </Space>
      </Row>

      <Row style={{ marginTop: 24 }}>
        <Col span={24} style={{ textAlign: 'right' }}>
          <CSVImportButton
            buttonLabel="Importer un fichier CSV"
            onConfirm={handleImportConfirm}
          />
        </Col>
      </Row>

      <Divider style={{ margin: '16px 0 24px' }} />

      <Form form={form} onFinish={onFinish} labelAlign="right">
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Form.Item
              label="Titre"
              name="title"
              rules={[{ required: true, message: 'Champs obligatoire.' }]}
              labelCol={{ flex: '110px' }}
              wrapperCol={{ flex: 1 }}
              style={{ marginBottom: 0 }}
            >
              <Input placeholder="Saisir un titre" />
            </Form.Item>
          </Col>

          <Col xs={24} md={12} lg={{ flex: '1 1 20%' }}>
            <Form.Item
              label="Date de bebut"
              name="startAt"
              rules={[{ required: true, message: 'Champs obligatoire.' }]}
              labelCol={{ flex: '110px' }}
              wrapperCol={{ flex: 1 }}
              style={{ marginBottom: 0 }}
            >
              <Select
                showSearch
                allowClear
                placeholder="Sélectionner une colonne"
                options={headerOptions}
                disabled={headerOptions.length === 0}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12} lg={{ flex: '1 1 20%' }}>
            <Form.Item
              label="Date de fin"
              name="endAt"
              rules={[{ required: true, message: 'Champs obligatoire.' }]}
              labelCol={{ flex: '110px' }}
              wrapperCol={{ flex: 1 }}
              style={{ marginBottom: 0 }}
            >
              <Select
                showSearch
                allowClear
                placeholder="Sélectionner une colonne"
                options={headerOptions}
                disabled={headerOptions.length === 0}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12} lg={{ flex: '1 1 20%' }}>
            <Form.Item
              label="Client"
              name="tenant"
              rules={[{ required: true, message: 'Champs obligatoire.' }]}
              labelCol={{ flex: '110px' }}
              wrapperCol={{ flex: 1 }}
              style={{ marginBottom: 0 }}
            >
              <Select
                showSearch
                allowClear
                placeholder="Sélectionner une colonne"
                options={headerOptions}
                disabled={headerOptions.length === 0}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12} lg={{ flex: '1 1 20%' }}>
            <Form.Item
              label="Montant"
              name="amount"
              rules={[{ required: true, message: 'Champs obligatoire.' }]}
              labelCol={{ flex: '110px' }}
              wrapperCol={{ flex: 1 }}
              style={{ marginBottom: 0 }}
            >
              <Select
                showSearch
                allowClear
                placeholder="Sélectionner une colonne"
                options={headerOptions}
                disabled={headerOptions.length === 0}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12} lg={{ flex: '1 1 20%' }}>
            <Form.Item
              label="Lot"
              name="lot_id"
              rules={[{ required: true, message: 'Champs obligatoire.' }]}
              labelCol={{ flex: '110px' }}
              wrapperCol={{ flex: 1 }}
              style={{ marginBottom: 0 }}
            >
              <Select
                showSearch
                allowClear
                placeholder="Sélectionner une colonne"
                options={headerOptions}
                disabled={headerOptions.length === 0}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12} lg={{ flex: '1 1 20%' }}>
            <Form.Item
              label="Format de date"
              name="format_date"
              rules={[{ required: true, message: 'Champs obligatoire.' }]}
              labelCol={{ flex: '110px' }}
              wrapperCol={{ flex: 1 }}
              style={{ marginBottom: 0 }}
            >
              <Select
                showSearch
                allowClear
                placeholder="Sélectionner un format"
                options={DATE_FORMAT_OPTIONS}
              />
            </Form.Item>
          </Col>

          <Col span={24} style={{ textAlign: 'right' }}>
            <Button type="primary" htmlType="submit" loading={loadingPost}>
              Valider
            </Button>
          </Col>
        </Row>
      </Form>

      <Divider style={{ margin: '16px 0 24px' }} />

      <Row style={{marginTop: 24}}>
        <Col span={24}>
          <Table
            dataSource={imports}
            columns={importColumns}
            loading={loadingGet}
            rowKey="id"
            pagination={false}
            locale={{emptyText: 'Aucun import enregistr\u00e9'}}
            expandable={{
              defaultExpandAllRows: true,
              rowExpandable: (record) => (
                Object.keys(record.parameters ?? {}).length > 0
              ),
              expandedRowRender: (record) => (
                <Table
                  dataSource={getParameterRows({ ...record.parameters, format_date: record.format_date })}
                  columns={parameterColumns}
                  rowKey="key"
                  pagination={false}
                  size="small"
                />
              ),
            }}
          />
        </Col>
      </Row>
    </div>
  );
};

export default ParamsComponent;
